import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import MoreMenuSheet from '@/components/layout/MoreMenuSheet';
import usePushNotifications from '@/hooks/usePushNotifications';

/**
 * Single source of truth for the mobile "More" menu overlay.
 *
 * Renders the dimmed backdrop + animated MoreMenuSheet, and internally
 * manages the data the sheet needs:
 *   - unread Sk8 Shop / Hire Hub inquiry badges
 *   - push-notification subscribe state
 *   - swipe-up-to-close gesture
 *
 * Pages just pass:
 *   - open / onClose  -> visibility
 *   - user            -> profile hero + auth-gated rows
 *   - crisisZoneVisible / isStandalone -> for hiding/showing rows
 *   - onInstall       -> page decides how to trigger PWA install
 *   - onLogout        -> page decides logout flow
 */
export default function MobileMenuOverlay({
  open,
  onClose,
  user,
  crisisZoneVisible = false,
  isStandalone = false,
  onInstall,
  onLogout,
}) {
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: pushSubscribe } = usePushNotifications();
  const [pushLoading, setPushLoading] = useState(false);
  const [listingInquiries, setListingInquiries] = useState({ sk8shop: 0, hire: 0, total: 0 });
  const [activeBookingsCount, setActiveBookingsCount] = useState(0);

  const touchStartY = useRef(0);
  const touchEndY = useRef(0);

  // Poll unread listing inquiries every 30s while logged in
  useEffect(() => {
    if (!user) { setListingInquiries({ sk8shop: 0, hire: 0, total: 0 }); return; }
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/conversations/unread-listing-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setListingInquiries(data);
      } catch (_) { /* ignore */ }
    };
    fetchCount();
    const int = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(int); };
  }, [user]);

  // Poll active bookings count for the "My Bookings" menu row badge.
  // Active = anything not completed/cancelled (pending, confirmed, en_route,
  // passenger_collected). 60s poll keeps the badge fresh without hammering.
  useEffect(() => {
    if (!user) { setActiveBookingsCount(0); return; }
    let cancelled = false;
    const ACTIVE_STATUSES = ['pending', 'confirmed', 'en_route', 'passenger_collected'];
    const fetchBookings = async () => {
      try {
        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.bookings || []);
        const count = list.filter(b => ACTIVE_STATUSES.includes(b.status)).length;
        if (!cancelled) setActiveBookingsCount(count);
      } catch (_) { /* ignore */ }
    };
    fetchBookings();
    const int = setInterval(fetchBookings, 60000);
    return () => { cancelled = true; clearInterval(int); };
  }, [user]);

  const handlePushSubscribe = async () => {
    toast.info('Setting up notifications...');
    setPushLoading(true);
    try {
      const result = await pushSubscribe();
      if (result) toast.success('Notifications enabled!');
      else toast.error('Could not enable notifications. Check your browser settings.');
    } catch (err) {
      toast.error('Notification setup failed: ' + (err.message || 'Unknown error'));
    } finally {
      setPushLoading(false);
    }
  };

  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchMove = (e) => { touchEndY.current = e.touches[0].clientY; };
  const handleTouchEnd = () => {
    const swipeDistance = touchStartY.current - touchEndY.current;
    if (swipeDistance > 50) onClose?.();
  };

  // Lock body scroll while the menu is open so background pages can't be
  // dragged behind it. Restores the previous scroll position on close.
  useEffect(() => {
    if (!open) return undefined;
    const scrollY = window.scrollY;
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[999] md:hidden" onClick={onClose} data-testid="mobile-menu-backdrop" />
      <MoreMenuSheet
        user={user}
        listingInquiries={listingInquiries}
        activeBookingsCount={activeBookingsCount}
        crisisZoneVisible={crisisZoneVisible}
        pushSupported={pushSupported}
        pushSubscribed={pushSubscribed}
        pushLoading={pushLoading}
        isStandalone={isStandalone}
        onPushSubscribe={handlePushSubscribe}
        onInstall={() => { onInstall?.(); onClose?.(); }}
        onLogout={() => { onClose?.(); onLogout?.(); }}
        onClose={onClose}
        handleTouchStart={handleTouchStart}
        handleTouchMove={handleTouchMove}
        handleTouchEnd={handleTouchEnd}
      />
    </>
  );
}
